import { useTranslation } from "react-i18next";
import type { RunArtifact } from "~/api/opsTypes";
import { Pill } from "~/features/automation/components";
import { formatBytes } from "~/features/automation/format";
import { Button } from "~/foundation/components/Button";
import { Empty } from "~/foundation/components/Empty";
import { formatRelativeTime } from "~/foundation/utils/time";

export interface RunArtifactsTabProps {
  artifacts: RunArtifact[];
  onView: (artifact: RunArtifact) => void;
  onPublish: (artifact: RunArtifact) => void;
}

/**
 * What the run produced, as the executor's own audit report describes it.
 *
 * The Content column is the load-bearing one. `withheld` means the bytes never reached the
 * server (published by reference), `sensitive` means reading them needs the flow's `run` grant
 * rather than `read` and the allow is audited — two different facts that a single "unavailable"
 * would flatten into one shrug.
 */
export function RunArtifactsTab({ artifacts, onView, onPublish }: RunArtifactsTabProps) {
  const { t, i18n } = useTranslation();

  if (artifacts.length === 0) {
    return (
      <Empty
        message={t(
          "No artifacts reported for this run. Rows appear when the executor uploads its artifact audit — older runs and purely local runs have none.",
        )}
      />
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-body">
        <thead>
          <tr className="border-b border-divider text-small text-text-tertiary">
            <th className="py-1.5 pe-3 text-start font-medium">{t("Path")}</th>
            <th className="py-1.5 pe-3 text-start font-medium">{t("Version")}</th>
            <th className="py-1.5 pe-3 text-start font-medium">{t("Bytes")}</th>
            <th className="py-1.5 pe-3 text-start font-medium">{t("Type")}</th>
            <th className="py-1.5 pe-3 text-start font-medium">{t("Produced by")}</th>
            <th className="py-1.5 pe-3 text-start font-medium">{t("Content")}</th>
            <th className="py-1.5 text-start font-medium">{t("Actions")}</th>
          </tr>
        </thead>
        <tbody>
          {artifacts.map((artifact) => {
            const withheld = !artifact.hasContent;
            return (
              <tr key={`${artifact.path}@${artifact.version}`} className="border-b border-divider">
                <td className="py-2 pe-3">
                  <button
                    type="button"
                    onClick={() => onView(artifact)}
                    className="text-start font-mono text-small text-accent hover:underline"
                    dir="ltr"
                  >
                    {artifact.path}
                  </button>
                </td>
                <td className="py-2 pe-3 tabular-nums text-small text-text-secondary">
                  {artifact.version}
                </td>
                <td className="py-2 pe-3 tabular-nums text-small text-text-secondary">
                  {formatBytes(artifact.bytes)}
                </td>
                <td className="py-2 pe-3 text-small text-text-secondary">
                  {artifact.mediaType ?? "—"}
                </td>
                <td className="py-2 pe-3 text-small text-text-secondary">
                  {artifact.producedBy ?? artifact.nodeId ?? "—"}
                  <span className="block text-text-tertiary">
                    {formatRelativeTime(artifact.createdAt, i18n.language)}
                  </span>
                </td>
                <td className="py-2 pe-3">
                  <span className="flex flex-wrap items-center gap-1">
                    {artifact.sensitive ? <Pill tone="warning">{t("sensitive")}</Pill> : null}
                    {withheld ? <Pill tone="neutral">{t("withheld")}</Pill> : null}
                    {!artifact.sensitive && !withheld ? (
                      <span className="text-small text-text-tertiary">{t("mirrored")}</span>
                    ) : null}
                  </span>
                </td>
                <td className="py-2">
                  <span className="flex items-center gap-1.5">
                    <Button size="sm" variant="neutral" onClick={() => onView(artifact)}>
                      {t("View")}
                    </Button>
                    <span
                      className="inline-flex"
                      title={
                        withheld
                          ? t(
                              "Cannot publish — the content is not on the server (bigger artifacts publish by reference only).",
                            )
                          : undefined
                      }
                    >
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={withheld}
                        onClick={() => onPublish(artifact)}
                      >
                        {t("Save to KnowIt…")}
                      </Button>
                    </span>
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
